import { elements } from '../domElements.js';
import { globalState } from '../state.js';
import { sendWebSocketMessage, initializeWebSocket as initMainWebSocket } from '../websocketService.js';
import { initializeVideoPlayback, stopVideoPlayback } from '../services/videoPlaybackService.js';
import { appendLog, updateStatus } from '../loggerService.js';
import { sendAdbCommandToServer } from '../services/adbClientService.js';
import { handleStreamingStopped } from '../messageHandlers.js';

/**
 * 获取流媒体设置
 * @returns {Object} 包含所有流媒体参数的配置对象
 */
function getStreamSettings() {
    return {
        maxFps: parseInt(elements.maxFpsSelect.value) || 0,
        bitrate: ((!isNaN(parseInt(elements.customBitrateInput.value.trim())) && parseInt(elements.customBitrateInput.value.trim()) > 0) ?
            parseInt(elements.customBitrateInput.value.trim()) : parseInt(elements.bitrateSelect.value)) * 1000000,
        enableAudio: elements.enableAudioInput.checked,
        enableControl: elements.enableControlInput.checked,
        video: true,
        noPowerOn: elements.noPowerOnInput.checked,
        turnScreenOff: elements.turnScreenOffInput.checked,
        powerOffOnClose: elements.powerOffOnCloseInput.checked,
        displayMode: globalState.currentDisplayMode,
        rotationLock: elements.rotationLockSelect.value,
        resolution: elements.customResolutionInput.value.trim() || elements.resolutionSelect.value,
        dpi: elements.customDpiInput.value.trim() || elements.dpiSelect.value,
        decoderType: elements.decoderTypeSelect.value, 
    };
}

/**
 * 开始屏幕镜像流
 * 
 * 根据选择的设备和显示模式，初始化并启动屏幕镜像流。
 * 支持不同的显示模式：覆盖层模式和原生任务栏模式。
 */
async function startStreaming() {
	if (!globalState.selectedDeviceId) {
		alert('请选择 ADB 设备。');
		return;
	}
	if (globalState.isRunning) return;
	if (!globalState.ws || globalState.ws.readyState !== WebSocket.OPEN) {
		appendLog("WebSocket未连接。无法启动。", true);
		return;
	}

	globalState.isRunning = true;
    globalState.decoderType = elements.decoderTypeSelect.value;
    globalState.controlEnabledAtStart = elements.enableControlInput.checked;
	updateDisplayOptionsOnStreamStart();

	const streamSettings = getStreamSettings();
    const startMessage = {
        action: 'start',
        deviceId: globalState.selectedDeviceId,
        ...streamSettings
    };

    try {
		if (globalState.currentDisplayMode === 'overlay') {
			if (streamSettings.resolution === "reset" || streamSettings.dpi === "reset") {
				throw new Error("覆盖层模式必须设置分辨率和DPI。");
			}
			updateStatus("覆盖层模式：正在获取初始显示列表...");
			const initialDisplaysResponse = await sendAdbCommandToServer({ commandType: 'getDisplayList' });
			const initialDisplayIds = initialDisplaysResponse.data.map(d => d.id);

			updateStatus(`覆盖层模式：正在设置覆盖层显示为 ${streamSettings.resolution}/${streamSettings.dpi}...`);
			await sendAdbCommandToServer({ commandType: 'setOverlay', resolution: streamSettings.resolution, dpi: streamSettings.dpi });

			updateStatus("覆盖层模式：正在获取更新后的显示列表...");
			await new Promise(resolve => setTimeout(resolve, 2000));
			const updatedDisplaysResponse = await sendAdbCommandToServer({ commandType: 'getDisplayList' });
			const updatedDisplayIds = updatedDisplaysResponse.data.map(d => d.id);

			const newDisplayIds = updatedDisplayIds.filter(id => !initialDisplayIds.includes(id));
			if (newDisplayIds.length === 0) throw new Error("叠加模式：找不到新的显示ID。");
			startMessage.overlayDisplayId = newDisplayIds[0];
			updateStatus(`叠加模式：使用新的显示ID ${startMessage.overlayDisplayId}`);

		} else if (globalState.currentDisplayMode === 'native_taskbar') {
			updateStatus("原生任务栏模式：正在设置窗口管理器属性...");
            let finalResolution = streamSettings.resolution;
            let finalDpi = streamSettings.dpi;
            let originalWidth = null, originalHeight = null;

            if (finalResolution !== "reset" && finalResolution.includes('x')) {
                [originalWidth, originalHeight] = finalResolution.split('x').map(Number);
                finalResolution = `${originalHeight}x${originalWidth}`;
                appendLog(`为原生任务栏模式翻转分辨率为  ${finalResolution}`);
            }
             if (originalHeight !== null && finalDpi !== "reset") {
                const currentDpiValue = parseInt(finalDpi, 10);
                if (!isNaN(currentDpiValue)) {
                    const targetSmallestWidth = 600;
                    const calculatedMagicDpi = Math.round((originalHeight / targetSmallestWidth) * 160);
                    if (currentDpiValue > calculatedMagicDpi) {
                        finalDpi = calculatedMagicDpi.toString();
                        appendLog(`原始DPI ${currentDpiValue} 调整为魔法DPI ${finalDpi}，以适配高度 ${originalHeight}.`);
                    }
                }
            }
            if (finalResolution !== "reset") {
                appendLog(`尝试设置窗口管理器大小为 ${finalResolution}...`);
                await sendAdbCommandToServer({ commandType: 'setWmSize', resolution: finalResolution });
            }
            if (finalDpi !== "reset") {
                appendLog(`尝试设置窗口管理器密度为 ${finalDpi}...`);
                await sendAdbCommandToServer({ commandType: 'setWmDensity', dpi: finalDpi });
            }
			updateStatus("原生任务栏模式：窗口管理器属性已设置。");
            startMessage.resolution = finalResolution;
            startMessage.dpi = finalDpi;
		}
        initializeVideoPlayback();
		sendWebSocketMessage(startMessage);
	} catch (error) {
		appendLog(`预启动ADB命令期间发生错误： ${error.message}`, true);
		stopStreamingAndCleanup(false);
	}
}

/**
 * 停止流媒体并清理资源
 * @param {boolean} [sendDisconnect=true] 是否发送断开连接消息
 */
export async function stopStreamingAndCleanup(sendDisconnect = true) {
    const wasRunning = globalState.isRunning;
    const previousDisplayMode = globalState.currentDisplayMode;
    const deviceToClean = globalState.selectedDeviceId;

    if (!globalState.isRunning && !sendDisconnect && !(globalState.ws && globalState.ws.readyState < WebSocket.CLOSING)) return;

    if (globalState.ws && globalState.ws.readyState === WebSocket.OPEN && sendDisconnect) {
        sendWebSocketMessage({ action: 'disconnect' });
    }

    handleStreamingStopped(sendDisconnect);

    if (wasRunning && deviceToClean && (previousDisplayMode === 'overlay' || previousDisplayMode === 'native_taskbar')) {
        updateStatus(`正在为 ${deviceToClean} 上的 ${previousDisplayMode} 模式清理ADB设置...`);
        try {
            await sendAdbCommandToServer({ commandType: 'cleanupAdb', mode: previousDisplayMode });
            updateStatus("ADB清理完成。");
        } catch (error) {
            appendLog(`ADB清理期间发生错误： ${error.message}`, true);
        }
    }
}

/**
 * 请求ADB设备列表
 */
export function requestAdbDevices() {
	if (globalState.ws && globalState.ws.readyState === WebSocket.OPEN) {
		sendWebSocketMessage({ action: 'getAdbDevices' });
		if (elements.refreshButton) elements.refreshButton.disabled = true;
	} else {
		populateDeviceSelect([]);
        if (elements.refreshButton) elements.refreshButton.disabled = false;
	}
}

/**
 * 填充设备选择下拉列表
 * @param {Array} devices - ADB设备列表
 */
export function populateDeviceSelect(devices) {
	elements.adbDevicesSelect.innerHTML = '';
	globalState.adbDevices = devices || [];
	if (globalState.adbDevices.length === 0) {
		const option = document.createElement('option');
		option.value = ''; option.textContent = '未找到ADB设备';
		elements.adbDevicesSelect.appendChild(option);
		elements.adbDevicesSelect.disabled = true;
		globalState.selectedDeviceId = null;
	} else {
		const defaultOption = document.createElement('option');
		defaultOption.value = ''; defaultOption.textContent = '-- 选择设备 --';
		elements.adbDevicesSelect.appendChild(defaultOption);
		globalState.adbDevices.forEach(device => {
			const option = document.createElement('option');
			option.value = device.id;
			option.textContent = device.model ? `${device.model} (${device.id})` : device.id;
			if (device.type !== 'device') {
				option.textContent += ` (${device.type})`;
				option.disabled = true;
			}
			elements.adbDevicesSelect.appendChild(option);
		});
		elements.adbDevicesSelect.disabled = false;
		const previouslySelected = globalState.selectedDeviceId;
        const isValidPrevious = previouslySelected && globalState.adbDevices.some(d => d.id === previouslySelected && d.type === 'device');
		if (isValidPrevious) {
            elements.adbDevicesSelect.value = previouslySelected;
        } else {
            const firstAvailable = globalState.adbDevices.find(d => d.type === 'device');
            if (firstAvailable) {
                elements.adbDevicesSelect.value = firstAvailable.id;
                globalState.selectedDeviceId = firstAvailable.id;
            } else {
                elements.adbDevicesSelect.value = '';
                globalState.selectedDeviceId = null;
            }
        }
	}
    updateDisplayOptionsState();
}

/**
 * 更新标签的视觉状态（启用/禁用）
 * @param {HTMLElement} label - 标签元素
 * @param {HTMLElement} inputElement - 关联的输入元素
 * @param {boolean} [isSpecialClass=false] - 是否使用特殊样式类
 */
function updateLabelVisualState(label, inputElement, isSpecialClass = false) {
    if (label && inputElement) {
        const isDisabled = inputElement.disabled;
        const activeClass = isSpecialClass ? 'disabled-label' : 'disabled';
        const inactiveClass = isSpecialClass ? 'disabled' : 'disabled-label';

        label.classList.toggle(activeClass, isDisabled);
        if (isDisabled) label.classList.remove(inactiveClass);
        else label.classList.remove(activeClass);
    }
}

/**
 * 更新显示选项的状态（启用/禁用）
 */
export function updateDisplayOptionsState() {
    const isStreaming = globalState.isRunning;
    const deviceSelected = !!globalState.selectedDeviceId;
    const canInteractWithOptions = !isStreaming && deviceSelected;
    const enableControlChecked = elements.enableControlInput.checked;
    const mode = globalState.currentDisplayMode;
    const isDex = mode === 'dex';
    const isNative = mode === 'native_taskbar';
    const isDefault = mode === 'default';

    if (elements.decoderTypeSelect) elements.decoderTypeSelect.disabled = isStreaming;
    const controlsToUpdate = [
        { els: [elements.bitrateSelect, elements.customBitrateInput], enable: canInteractWithOptions },
        { els: [elements.maxFpsSelect], enable: canInteractWithOptions },
        { els: [elements.noPowerOnInput], enable: canInteractWithOptions, label: elements.noPowerOnLabel },
        { els: [elements.enableAudioInput], enable: canInteractWithOptions, label: elements.enableAudioLabel},
        { els: [elements.enableControlInput], enable: canInteractWithOptions, label: elements.enableControlLabel},
        { els: [elements.turnScreenOffInput], enable: canInteractWithOptions && enableControlChecked, label: elements.turnScreenOffLabel, isSpecialClass: true },
        { els: [elements.powerOffOnCloseInput], enable: canInteractWithOptions && enableControlChecked, label: elements.powerOffOnCloseLabel, isSpecialClass: true },
        { els: Array.from(elements.displayModeCheckboxes), enable: canInteractWithOptions },
        { els: [elements.resolutionSelect, elements.customResolutionInput], enable: canInteractWithOptions && !isDex && !isDefault, label: elements.resolutionLabel },
        { els: [elements.dpiSelect, elements.customDpiInput], enable: canInteractWithOptions && !isDex && !isDefault, label: elements.dpiLabel },
        { els: [elements.rotationLockSelect], enable: canInteractWithOptions && !isDex && !isNative, label: elements.rotationLockLabel },
    ];

    controlsToUpdate.forEach(config => {
        const elementsToProcess = Array.isArray(config.els) ? config.els : [config.els];
        elementsToProcess.forEach(el => { if (el) el.disabled = !config.enable; });
        if (config.label && elementsToProcess[0]) {
            updateLabelVisualState(config.label, elementsToProcess[0], config.isSpecialClass);
        }
    });

    if (elements.startButton) elements.startButton.disabled = isStreaming || !deviceSelected;
    if (elements.stopButton) elements.stopButton.disabled = !isStreaming;
    if (elements.adbDevicesSelect) elements.adbDevicesSelect.disabled = isStreaming;
    if (elements.refreshButton) elements.refreshButton.disabled = isStreaming || !(globalState.ws && globalState.ws.readyState === WebSocket.OPEN);


    if (isStreaming) {
        const streamDisableConfig = [
            elements.resolutionSelect, elements.customResolutionInput, elements.dpiSelect, elements.customDpiInput,
            elements.bitrateSelect, elements.customBitrateInput, elements.maxFpsSelect, elements.rotationLockSelect,
            elements.noPowerOnInput, elements.turnScreenOffInput, elements.powerOffOnCloseInput, elements.decoderTypeSelect,
            elements.enableAudioInput, elements.enableControlInput, ...Array.from(elements.displayModeCheckboxes)
        ];
        streamDisableConfig.forEach(el => { if(el) el.disabled = true; });

        updateLabelVisualState(elements.resolutionLabel, elements.resolutionSelect);
        updateLabelVisualState(elements.dpiLabel, elements.dpiSelect);
        updateLabelVisualState(elements.rotationLockLabel, elements.rotationLockSelect);
        updateLabelVisualState(elements.noPowerOnLabel, elements.noPowerOnInput);
        updateLabelVisualState(elements.turnScreenOffLabel, elements.turnScreenOffInput, true);
        updateLabelVisualState(elements.powerOffOnCloseLabel, elements.powerOffOnCloseInput, true);
        updateLabelVisualState(elements.enableAudioLabel, elements.enableAudioInput);
        updateLabelVisualState(elements.enableControlLabel, elements.enableControlInput);
    }
}

/**
 * 流媒体开始时更新显示选项状态
 */
export function updateDisplayOptionsOnStreamStart() {
    if(elements.startButton) elements.startButton.disabled = true;
    if(elements.stopButton) elements.stopButton.disabled = false;
    if(elements.adbDevicesSelect) elements.adbDevicesSelect.disabled = true;
    if(elements.refreshButton) elements.refreshButton.disabled = true;
    updateDisplayOptionsState();
}

/**
 * 流媒体停止时更新显示选项状态
 */
export function updateDisplayOptionsOnStreamStop() {
    if(elements.stopButton) elements.stopButton.disabled = true;
    if(elements.adbDevicesSelect) elements.adbDevicesSelect.disabled = false;
    if(elements.refreshButton && globalState.ws && globalState.ws.readyState === WebSocket.OPEN) {
        elements.refreshButton.disabled = false;
    } else if (elements.refreshButton) {
        elements.refreshButton.disabled = true;
    }
    if(elements.startButton) elements.startButton.disabled = !globalState.selectedDeviceId;
    updateDisplayOptionsState();
}

/**
 * 初始化侧边栏控件
 */
export function initSidebarControls() {
    elements.startButton.addEventListener('click', startStreaming);
    elements.stopButton.addEventListener('click', () => stopStreamingAndCleanup(true));
    elements.refreshButton.addEventListener('click', () => {
        if (globalState.ws && globalState.ws.readyState === WebSocket.OPEN) requestAdbDevices();
	    else initMainWebSocket();
    });

    elements.adbDevicesSelect.onchange = () => {
		const selectedId = elements.adbDevicesSelect.value;
		const selectedDevice = globalState.adbDevices.find(d => d.id === selectedId);
		globalState.selectedDeviceId = (selectedDevice && selectedDevice.type === 'device') ? selectedId : null;
		updateDisplayOptionsState();
	};

    elements.displayModeCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
            if (checkbox.checked) {
                globalState.currentDisplayMode = checkbox.value;
                elements.displayModeCheckboxes.forEach(cb => { if (cb !== checkbox) cb.checked = false; });
            }
            updateDisplayOptionsState();
        });
    });

    elements.enableControlInput.addEventListener('change', function() {
        if (!this.checked) {
            elements.turnScreenOffInput.checked = false;
            elements.powerOffOnCloseInput.checked = false;
        }
        updateDisplayOptionsState();
    });

    elements.decoderTypeSelect.addEventListener('change', () => {
        globalState.decoderType = elements.decoderTypeSelect.value;
    });

    populateDeviceSelect([]);
    updateDisplayOptionsState();
}